import { useMemo } from "react";
import type { CategoryDto } from "@/api/categories";
import type { ProjectDto } from "@/api/projects";
import type { TicketDto } from "@/api/tickets";
import { useRequest } from "@/request";

export type IntakeValues = Record<string, unknown>;
export type IntakeCondition = {
  field?: string;
  operator?: "equals" | "in" | "filled";
  value?: unknown;
  values?: unknown[];
  all?: IntakeCondition[];
  any?: IntakeCondition[];
};
export type IntakeOption = { value: string; label: string; data?: IntakeValues };
export type IntakeField = {
  key: string;
  label: string;
  helpText?: string;
  type: "text" | "textarea" | "number" | "money" | "date" | "select" | "multiselect" | "boolean" | "entitySelect";
  required: boolean;
  requiredWhen?: IntakeCondition | null;
  visibleWhen?: IntakeCondition | null;
  constraints?: { minLength?: number; maxLength?: number; min?: number; max?: number; scale?: number; currency?: string } | null;
  options: IntakeOption[];
  source?: string | null;
  dependsOn: string[];
  group?: string | null;
  order?: number;
};
export type IntakeFormSchema = { requiresCustomer: boolean; fields: IntakeField[] };
export type EffectiveIntakeFormDto = {
  formDefinitionId?: string | null;
  formVersionId?: string | null;
  version?: number | null;
  name: string;
  inheritedFromCategoryId?: string | null;
  schema: IntakeFormSchema;
};
export type IntakeFormDto = {
  id: string;
  code: string;
  name: string;
  draftSchema: IntakeFormSchema;
  publishedVersionId?: string | null;
  publishedVersion?: number | null;
  concurrencyStamp: string;
};
export type IntakeRequester = { name?: string | null; phone?: string | null; email?: string | null };
export type IntakeAttachmentDto = { id: string; fileName: string; size: number; scanStatus: number; scanFailureReason?: string | null };
export type TicketIntakeSessionDto = {
  id: string;
  projectId: string;
  categoryId?: string | null;
  formVersionId?: string | null;
  form: EffectiveIntakeFormDto;
  subject: string;
  body?: string | null;
  requester?: IntakeRequester | null;
  context: IntakeValues;
  fieldValues: IntakeValues;
  attachments: IntakeAttachmentDto[];
  expiresAt: string;
  submittedTicketId?: string | null;
  concurrencyStamp: string;
};
export type UpdateIntakeSessionInput = {
  formVersionId?: string | null;
  categoryId?: string | null;
  subject: string;
  body?: string | null;
  requester?: IntakeRequester | null;
  context: IntakeValues;
  fieldValues: IntakeValues;
  concurrencyStamp: string;
};
export type IntakeLookupResult = { items: IntakeOption[]; hasMore: boolean };
export type TicketIntakeSubmissionDto = {
  ticketId: string;
  formVersionId?: string | null;
  form: EffectiveIntakeFormDto;
  fieldValues: IntakeValues;
  displayValues: IntakeValues;
  facts: IntakeValues;
  creationTime: string;
};

const base = "ticket-intake";

export function useTicketIntakeApi() {
  const { request } = useRequest();
  return useMemo(
    () => ({
      projects: (signal?: AbortSignal) => request<ProjectDto[]>(`${base}/projects`, { method: "GET", signal, skipErrorToast: true }),
      categories: (projectId: string, signal?: AbortSignal) =>
        request<CategoryDto[]>(`${base}/categories?projectId=${encodeURIComponent(projectId)}`, { method: "GET", signal, skipErrorToast: true }),
      getForm: (projectId: string, categoryId?: string | null, signal?: AbortSignal) =>
        request<EffectiveIntakeFormDto>(
          `${base}/form?projectId=${encodeURIComponent(projectId)}${categoryId ? `&categoryId=${encodeURIComponent(categoryId)}` : ""}`,
          { method: "GET", signal, skipErrorToast: true },
        ),
      listForms: (signal?: AbortSignal) => request<IntakeFormDto[]>(`${base}/forms`, { method: "GET", signal, skipErrorToast: true }),
      createForm: (input: { code: string; name: string; schema: IntakeFormSchema }, options: { skipErrorToast?: boolean } = {}) =>
        request<IntakeFormDto>(`${base}/forms`, { method: "POST", data: input, ...options }),
      updateForm: (id: string, input: { code: string; name: string; schema: IntakeFormSchema; concurrencyStamp: string }) =>
        request<IntakeFormDto>(`${base}/forms/${id}`, { method: "PUT", data: input }),
      publishForm: (id: string, concurrencyStamp: string) =>
        request<IntakeFormDto>(`${base}/forms/${id}/publish`, { method: "POST", data: { concurrencyStamp } }),
      createSession: (
        input: ({ projectId: string; projectCode?: never } | { projectCode: string; projectId?: never }) & {
          categoryId?: string | null;
          context: IntakeValues;
        },
        signal?: AbortSignal,
      ) => request<TicketIntakeSessionDto>(`${base}/sessions`, { method: "POST", data: input, signal, skipErrorToast: true }),
      getSession: (id: string, signal?: AbortSignal) =>
        request<TicketIntakeSessionDto>(`${base}/sessions/${id}`, { method: "GET", signal, skipErrorToast: true }),
      updateSession: (id: string, input: UpdateIntakeSessionInput) =>
        request<TicketIntakeSessionDto>(`${base}/sessions/${id}`, { method: "PATCH", data: input, skipErrorToast: true }),
      lookup: (id: string, capability: string, input: { search?: string; skipCount?: number; maxResultCount?: number }, signal?: AbortSignal) =>
        request<IntakeLookupResult>(`${base}/sessions/${id}/lookups/${encodeURIComponent(capability)}`, {
          method: "POST",
          data: input,
          signal,
          skipErrorToast: true,
        }),
      upload: (id: string, file: File) => {
        const data = new FormData();
        data.append("file", file);
        return request<TicketIntakeSessionDto>(`${base}/sessions/${id}/attachments`, {
          method: "POST",
          data,
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 0,
          skipErrorToast: true,
        });
      },
      removeAttachment: (id: string, attachmentId: string, concurrencyStamp: string) =>
        request<TicketIntakeSessionDto>(`${base}/sessions/${id}/attachments/${attachmentId}`, {
          method: "DELETE",
          data: { concurrencyStamp },
          skipErrorToast: true,
        }),
      submit: (id: string, concurrencyStamp: string) =>
        request<TicketDto>(`${base}/sessions/${id}/submit`, { method: "POST", data: { concurrencyStamp }, skipErrorToast: true }),
      getSubmission: (ticketId: string, signal?: AbortSignal) =>
        request<TicketIntakeSubmissionDto | null>(`${base}/submissions/${ticketId}`, { method: "GET", signal, skipErrorToast: true }),
    }),
    [request],
  );
}
